/**
 * BuildingDealContext
 *
 * Building-specific deals context that follows the same architecture as PropertyDealContext
 * but uses building-specific API endpoints and data structures.
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
  useMemo,
  useEffect,
} from 'react'

// Define minimal types for now (same as PropertyDealContext)
interface Deal {
  id: string
  title: string
  [key: string]: any
}

interface CreateDealInput {
  title: string
  [key: string]: any
}

interface UpdateDealInput {
  id: string
  [key: string]: any
}

interface DealFilters {
  [key: string]: any
}

enum DealStatus {
  OPEN = 'open',
  WON = 'won',
  LOST = 'lost',
}

enum DealPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
  URGENT = 'urgent',
}

interface DealCRUDConfig {
  [key: string]: any
}
import { apiClient } from '@/services/apiClient'

// ============================================================================
// Types - Building-specific deal interfaces
// ============================================================================

/**
 * Building-specific deal interface extending the library Deal type
 */
export interface BuildingDeal extends Deal {
  // Legacy API fields for backward compatibility
  pipelineKanbanColumnId: string
  ownerUserId: string
  geoCenter?: string | null
  createTimestamp: string
  updateTimestamp: string
  archiveTimestamp?: string | null
  deleteTimestamp?: string | null
  isArchived?: boolean
  hash?: string | null
  shareInformations?: any
  shareInformationsExpiredTimestamp?: string

  // Building-specific fields
  buildingId?: string
  landId?: string
  propertyType?: 'building' | 'residential' | 'commercial' | 'industrial'

  // Contact information
  firstName?: string
  lastName?: string
  contact?: string
}

/**
 * Deal-Building link interface
 */
export interface DealBuilding {
  id: string
  pipelineKanbanDealId: string
  buildingId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp?: string | null
  isDeleted: boolean
  deal?: BuildingDeal
}

/**
 * Legacy create deal payload for API compatibility
 */
export interface CreateBuildingDealPayload {
  title: string
  columnId: string
  informations?: Record<string, any>
  status?: 'open' | 'won' | 'lost'
  participants?: Array<{ id: string; name: string; role?: string }>
}

/**
 * Legacy update deal payload
 */
export interface UpdateBuildingDealPayload {
  title?: string
  informations?: Record<string, any>
}

/**
 * Deal participant interface
 */
export interface DealParticipant {
  id: string
  pipelineKanbanDealId: string
  userId: string
  createTimestamp: string
  updateTimestamp: string
  deleteTimestamp?: string | null
  user: {
    id: string
    firstName: string
    lastName: string
    email: string
    photo: string
  }
}

/**
 * Building deals context state
 */
export interface BuildingDealContextState {
  // Building-specific fields
  buildingId: string | null
  buildingData: any | null
  deals: DealBuilding[]
  loading: boolean
  error: string | null
  total: number

  // Enhanced library integration
  buildingDeals: BuildingDeal[]
  filters: DealFilters
  selectedDeals: Set<string>

  // Building-specific methods
  fetchDeals: (buildingId: string) => Promise<void>
  addDeal: (dealData: CreateBuildingDealPayload) => Promise<void>
  updateDeal: (dealId: string, data: UpdateBuildingDealPayload) => Promise<void>
  deleteDeal: (dealId: string) => Promise<void>
  refreshDeals: () => Promise<void>
  linkDealToBuilding: (dealId: string, buildingId: string) => Promise<void>
  unlinkDealFromBuilding: (dealBuildingId: string) => Promise<void>
  fetchDealParticipants: (dealId: string) => Promise<DealParticipant[]>
  setBuildingData: (data: any) => void

  // Enhanced methods using library functionality
  createBuildingDeal: (input: CreateDealInput) => Promise<BuildingDeal>
  updateBuildingDeal: (input: UpdateDealInput) => Promise<BuildingDeal>
  deleteBuildingDeal: (dealId: string) => Promise<void>
  setFilters: (filters: DealFilters) => void
  selectDeal: (dealId: string) => void
  deselectDeal: (dealId: string) => void
  clearSelection: () => void
}

// ============================================================================
// Context Creation
// ============================================================================

const BuildingDealContext = createContext<BuildingDealContextState | undefined>(
  undefined
)

// ============================================================================
// Provider Props
// ============================================================================

export interface BuildingDealProviderProps {
  children: ReactNode
  buildingId?: string | null
  buildingData?: any | null
  enableLibraryFeatures?: boolean
}

// ============================================================================
// Provider Component
// ============================================================================

export const BuildingDealProvider: React.FC<BuildingDealProviderProps> = ({
  children,
  buildingId: initialBuildingId = null,
  buildingData: initialBuildingData = null,
  enableLibraryFeatures = true,
}) => {
  // Building-specific state
  const [buildingId, setBuildingId] = useState<string | null>(initialBuildingId)
  const [buildingData, setBuildingData] = useState<any | null>(
    initialBuildingData
  )
  const [deals, setDeals] = useState<DealBuilding[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Enhanced state
  const [filters, setFilters] = useState<DealFilters>({})
  const [selectedDeals, setSelectedDeals] = useState<Set<string>>(new Set())

  // ============================================================================
  // Data Transformation Functions
  // ============================================================================

  const transformApiDealToBuildingDeal = useCallback(
    (apiData: any): BuildingDeal => {
      return {
        // Library Deal fields
        id: String(apiData.id || apiData.pipelineKanbanDealId),
        title: apiData.title || '',
        description: apiData.informations?.description || '',
        amount: apiData.informations?.amount || 0,
        currency: apiData.informations?.currency || 'CHF',
        status: mapApiStatusToDealStatus(apiData.status),
        priority: mapApiPriorityToDealPriority(apiData.informations?.priority),
        ownerId: String(apiData.ownerUserId || ''),
        pipelineId: String(apiData.pipelineId || ''),
        stageId: String(apiData.pipelineKanbanColumnId || ''),
        createdAt: apiData.create_timestamp || new Date().toISOString(),
        updatedAt: apiData.update_timestamp || new Date().toISOString(),
        customFields: apiData.informations || {},

        // Building-specific legacy fields
        pipelineKanbanColumnId: String(apiData.pipelineKanbanColumnId || ''),
        ownerUserId: String(apiData.ownerUserId || ''),
        geoCenter: apiData.geo_center,
        createTimestamp: apiData.create_timestamp || new Date().toISOString(),
        updateTimestamp: apiData.update_timestamp || new Date().toISOString(),
        archiveTimestamp: apiData.archiveTimestamp,
        deleteTimestamp: apiData.deleteTimestamp,
        isArchived: apiData.isArchived || false,
        hash: apiData.hash,
        shareInformations: apiData.shareInformations,
        shareInformationsExpiredTimestamp:
          apiData.shareInformationsExpiredTimestamp,

        // Building-specific
        buildingId: String(buildingId || ''),
        propertyType: apiData.informations?.propertyType || 'building',
        firstName: apiData.firstName,
        lastName: apiData.lastName,
        contact: apiData.contact,
      }
    },
    [buildingId]
  )

  const transformBuildingDealToApiRequest = useCallback((deal: any): any => {
    return {
      title: deal.title,
      informations: {
        ...deal.customFields,
        ...deal.informations,
        description: deal.description,
        amount: deal.amount,
        currency: deal.currency,
        priority: deal.priority,
        property_type: deal.propertyType,
      },
      pipeline_kanban_column_id:
        deal.stageId || deal.columnId || deal.pipelineKanbanColumnId,
      owner_user_id: deal.ownerId || deal.ownerUserId,
    }
  }, [])

  // ============================================================================
  // Status/Priority Mapping Functions
  // ============================================================================

  const mapApiStatusToDealStatus = (apiStatus?: string): DealStatus => {
    switch (apiStatus) {
      case 'won':
        return DealStatus.WON
      case 'lost':
        return DealStatus.LOST
      case 'open':
        return DealStatus.OPEN
      default:
        return DealStatus.OPEN
    }
  }

  const mapApiPriorityToDealPriority = (apiPriority?: string): DealPriority => {
    switch (apiPriority) {
      case 'high':
        return DealPriority.HIGH
      case 'urgent':
        return DealPriority.URGENT
      case 'low':
        return DealPriority.LOW
      default:
        return DealPriority.MEDIUM
    }
  }

  // Deal library integration
  const dealCRUDConfig: DealCRUDConfig = useMemo(
    () => ({
      endpoints: {
        list: '/pipelines/pipelinekanbandealsbuildings/building',
        get: '/pipelines/kanbandeals/:id',
        create: '/pipelines/kanbandeals',
        update: '/pipelines/kanbandeals/:id',
        delete: '/pipelines/pipelinekanbandealsbuildings/:id',
      },
      apiClient,
      transformResponse: transformApiDealToBuildingDeal,
      transformRequest: transformBuildingDealToApiRequest,
      cache: { enabled: true, ttl: 5 * 60 * 1000 }, // 5 minutes
      optimisticUpdates: true,
      onError: (err) => setError(err.message),
    }),
    [transformApiDealToBuildingDeal, transformBuildingDealToApiRequest]
  )

  // Temporarily disable library integration to maintain consistency with PropertyDealContext
  const libraryState = {
    loading: false,
    error: null,
    deals: [],
    deal: null,
    total: 0,
    filters: {},
    sort: [],
    pagination: { page: 1, pageSize: 20 },
  }
  const libraryActions = {
    loadDeals: async () => {},
    loadDeal: async () => ({}) as any,
    createDeal: async () => ({}) as any,
    updateDeal: async () => ({}) as any,
    deleteDeal: async () => {},
    createDeals: async () => [],
    updateDeals: async () => [],
    deleteDeals: async () => {},
    setFilters: () => {},
    setSort: () => {},
    setPagination: () => {},
    refresh: async () => {},
    reset: () => {},
    clearCache: () => {},
    getCachedDeal: () => null,
  }

  // ============================================================================
  // Building-Specific Methods
  // ============================================================================

  const fetchDeals = useCallback(
    async (buildingIdParam: string) => {
      setLoading(true)
      setError(null)
      setBuildingId(buildingIdParam)

      try {
        const response = await apiClient.get(
          `/pipelines/pipelinekanbandealsbuildings/building/${buildingIdParam}`
        )

        const dealBuildings: DealBuilding[] = response.data || []
        setDeals(dealBuildings)

        // Update filters for library integration
        if (enableLibraryFeatures) {
          setFilters((prev) => ({
            ...prev,
            customFilters: { buildingId: buildingIdParam },
          }))
        }
      } catch (err: any) {
        console.error('Error fetching building deals:', err)
        setError(
          err.response?.data?.message || 'Failed to fetch building deals'
        )
        setDeals([])
      } finally {
        setLoading(false)
      }
    },
    [enableLibraryFeatures]
  )

  const addDeal = useCallback(
    async (dealData: CreateBuildingDealPayload) => {
      if (!buildingId) {
        setError('No building selected')
        return
      }

      setLoading(true)
      setError(null)

      try {
        // Create deal
        const createDealResponse = await apiClient.post(
          `/pipelines/kanbandeals`,
          {
            title: dealData.title,
            informations: dealData.informations || {},
            pipeline_kanban_column_id: dealData.columnId,
          }
        )

        const newDeal = createDealResponse.data

        // Link to building
        const linkResponse = await apiClient.post(
          '/pipelines/pipelinekanbandealsbuildings',
          {
            pipeline_kanban_deal_id: newDeal.id,
            building_id: buildingId,
          }
        )

        const newDealBuilding: DealBuilding = {
          ...linkResponse.data,
          deal: newDeal,
        }

        setDeals((prev) => [...prev, newDealBuilding])
      } catch (err: any) {
        console.error('Error creating building deal:', err)
        setError(
          err.response?.data?.message || 'Failed to create building deal'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [buildingId]
  )

  const updateDeal = useCallback(
    async (dealId: string, data: UpdateBuildingDealPayload) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.put(
          `/pipelines/kanbandeals/${dealId}`,
          data
        )
        const updatedDeal = response.data

        setDeals((prev) =>
          prev.map((dealBuilding) =>
            dealBuilding.pipelineKanbanDealId === dealId
              ? {
                  ...dealBuilding,
                  deal: { ...dealBuilding.deal, ...updatedDeal },
                }
              : dealBuilding
          )
        )
      } catch (err: any) {
        console.error('Error updating building deal:', err)
        setError(
          err.response?.data?.message || 'Failed to update building deal'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const deleteDeal = useCallback(
    async (dealBuildingId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-building association to get dealId and buildingId
        const dealBuilding = deals.find((d) => d.id === dealBuildingId)
        if (!dealBuilding) {
          throw new Error('Deal-building association not found')
        }

        // Unlink from building using new endpoint with dealId and buildingId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealsbuildings/deal/${dealBuilding.pipelineKanbanDealId}/building/${dealBuilding.buildingId}`
        )
        setDeals((prev) => prev.filter((d) => d.id !== dealBuildingId))
      } catch (err: any) {
        console.error('Error deleting building deal:', err)
        setError(
          err.response?.data?.message || 'Failed to delete building deal'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  const linkDealToBuilding = useCallback(
    async (dealId: string, buildingIdParam: string) => {
      setLoading(true)
      setError(null)

      try {
        const response = await apiClient.post(
          '/pipelines/pipelinekanbandealsbuildings',
          {
            pipeline_kanban_deal_id: dealId,
            building_id: buildingIdParam,
          }
        )

        const dealResponse = await apiClient.get(
          `/pipelines/kanbandeals/${dealId}`
        )

        const newDealBuilding: DealBuilding = {
          ...response.data,
          deal: dealResponse.data,
        }

        setDeals((prev) => [...prev, newDealBuilding])
      } catch (err: any) {
        console.error('Error linking deal to building:', err)
        setError(
          err.response?.data?.message || 'Failed to link deal to building'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    []
  )

  const unlinkDealFromBuilding = useCallback(
    async (dealBuildingId: string) => {
      setLoading(true)
      setError(null)

      try {
        // Find the deal-building association to get dealId and buildingId
        const dealBuilding = deals.find((d) => d.id === dealBuildingId)
        if (!dealBuilding) {
          throw new Error('Deal-building association not found')
        }

        // Unlink from building using new endpoint with dealId and buildingId
        await apiClient.delete(
          `/pipelines/pipelinekanbandealsbuildings/deal/${dealBuilding.pipelineKanbanDealId}/building/${dealBuilding.buildingId}`
        )
        setDeals((prev) => prev.filter((d) => d.id !== dealBuildingId))
      } catch (err: any) {
        console.error('Error unlinking deal from building:', err)
        setError(
          err.response?.data?.message || 'Failed to unlink deal from building'
        )
        throw err
      } finally {
        setLoading(false)
      }
    },
    [deals]
  )

  const fetchDealParticipants = useCallback(
    async (dealId: string): Promise<DealParticipant[]> => {
      try {
        const response = await apiClient.get(
          `/pipelines/kanbandealparticipants/deal/${dealId}?includeUsers=true`
        )
        return response.data || []
      } catch (err: any) {
        console.error('Error fetching building deal participants:', err)
        return []
      }
    },
    []
  )

  const refreshDeals = useCallback(async () => {
    if (buildingId) {
      await fetchDeals(buildingId)
    }
  }, [buildingId, fetchDeals])

  // ============================================================================
  // Enhanced Methods Using Library Functionality
  // ============================================================================

  const createBuildingDeal = useCallback(
    async (input: CreateDealInput): Promise<BuildingDeal> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      // Enhance input with building-specific data
      const enhancedInput: CreateDealInput = {
        ...input,
        custom_fields: {
          ...input.customFields,
          building_id: buildingId,
          property_type: 'building',
        },
      }

      const deal = await libraryActions.createDeal(enhancedInput)

      // Also create link if buildingId is available
      if (buildingId) {
        await linkDealToBuilding(deal.id, buildingId)
      }

      return deal as BuildingDeal
    },
    [enableLibraryFeatures, libraryActions, buildingId, linkDealToBuilding]
  )

  const updateBuildingDeal = useCallback(
    async (input: UpdateDealInput): Promise<BuildingDeal> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      const deal = await libraryActions.updateDeal(input)
      return deal as BuildingDeal
    },
    [enableLibraryFeatures, libraryActions]
  )

  const deleteBuildingDeal = useCallback(
    async (dealId: string): Promise<void> => {
      if (!enableLibraryFeatures) {
        throw new Error('Library features not enabled')
      }

      await libraryActions.deleteDeal(dealId)
    },
    [enableLibraryFeatures, libraryActions]
  )

  // ============================================================================
  // Selection Methods
  // ============================================================================

  const selectDeal = useCallback((dealId: string) => {
    setSelectedDeals((prev) => new Set([...prev, dealId]))
  }, [])

  const deselectDeal = useCallback((dealId: string) => {
    setSelectedDeals((prev) => {
      const newSet = new Set(prev)
      newSet.delete(dealId)
      return newSet
    })
  }, [])

  const clearSelection = useCallback(() => {
    setSelectedDeals(new Set())
  }, [])

  // ============================================================================
  // Computed Values
  // ============================================================================

  const buildingDeals = useMemo(() => {
    return deals
      .map((dealBuilding) =>
        dealBuilding.deal
          ? transformApiDealToBuildingDeal(dealBuilding.deal)
          : null
      )
      .filter(Boolean) as BuildingDeal[]
  }, [deals, transformApiDealToBuildingDeal])

  // ============================================================================
  // Context Value
  // ============================================================================

  const contextValue: BuildingDealContextState = {
    // Building-specific state
    buildingId,
    buildingData,
    deals,
    loading: loading || libraryState.loading,
    error: error || libraryState.error?.message || null,
    total: deals.length,

    // Enhanced state
    buildingDeals,
    filters,
    selectedDeals,

    // Building-specific methods
    fetchDeals,
    addDeal,
    updateDeal,
    deleteDeal,
    refreshDeals,
    linkDealToBuilding,
    unlinkDealFromBuilding,
    fetchDealParticipants,
    setBuildingData,

    // Enhanced methods
    createBuildingDeal,
    updateBuildingDeal,
    deleteBuildingDeal,
    setFilters,
    selectDeal,
    deselectDeal,
    clearSelection,
  }

  return (
    <BuildingDealContext.Provider value={contextValue}>
      {children}
    </BuildingDealContext.Provider>
  )
}

// ============================================================================
// Custom Hook
// ============================================================================

export const useBuildingDeals = () => {
  const context = useContext(BuildingDealContext)
  if (context === undefined) {
    throw new Error(
      'useBuildingDeals must be used within a BuildingDealProvider'
    )
  }
  return context
}
